import { useState } from 'react';
import { IconFilePlus, IconPrinter } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, useToast } from '@hana/ui';
import { ISSUE_EMPS, ISSUE_TEMPLATES, annualLeaveDays, gateOf, issueView, workHoursText, type EmpKey, type IssueInput } from './issue';
import './contractIssue.i18n';
import s from './hr.module.css';
import c from './contractIssue.module.css';

// Mint-LaborContract · tab Phát hành (code board khối tabIss). Chỉ đọc; nút ghi báo toast. Lương che (cần quyền lương), không CCCD.
const TONE = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', mute: s.tMute ?? '' };

export function ContractIssue() {
  const t = useT();
  const toast = useToast();
  const [emp, setEmp] = useState<EmpKey>('LanVT');
  const [tplPick, setTplPick] = useState<string | null>(null);
  const [inp, setInp] = useState<IssueInput>({});
  const e = ISSUE_EMPS.find((x) => x[0] === emp) ?? ISSUE_EMPS[0]!;
  const tpl = tplPick ?? e[6];
  const v = issueView(e, tpl, inp);
  const gateMsg = v.gate.block || v.gate.warns.join(' · ');
  const name = e[1].replace(' (mẫu)', '');
  const title = tpl === 'HDTV-02M' ? t('ci.tTrial') : tpl === 'HDDT-06M' ? t('ci.tTrain') : t('ci.tLabor');
  const pickEmp = (u: EmpKey) => { setEmp(u); setTplPick(null); setInp({}); };
  const fields: { k: string; val: string; key?: keyof IssueInput; mask?: boolean }[] = [
    { k: t('ci.fNo'), val: v.soHD },
    { k: t('ci.fTimes'), val: String(v.kyLan) },
    { k: t('ci.fSign'), val: inp.sign ?? '30/Oct/2026', key: 'sign' },
    { k: t('ci.fFrom'), val: v.from, key: 'from' },
    { k: t('ci.fTo'), val: v.to },
    { k: t('ci.fSalary'), val: t('ci.needMoney'), mask: true },
  ];
  const hl = (x: string) => <span className={c.hl}>{x}</span>;
  return (
    <div className={c.grid}>
      <div className={c.col}>
        <section className={s.card} aria-label={t('ci.a1')}>
          <h2 className={c.h}>{t('ci.h1')}</h2>
          <div className={c.empRow}>
            <label className={c.lab}>
              <div>{t('ci.issueFor')}</div>
              <select aria-label={t('ci.staff')} className={c.ctl} value={emp} onChange={(ev) => pickEmp(ev.target.value as EmpKey)}>
                {ISSUE_EMPS.map((o) => <option key={o[0]} value={o[0]}>{o[1]} · {o[2]}</option>)}
              </select>
            </label>
            <div className={c.chips}>{v.chips.map((ch) => <span key={ch.k} className={`${s.stChip} ${TONE[ch.tone]}`}>{ch.k}</span>)}</div>
          </div>
        </section>
        <section className={s.card} aria-label={t('ci.a2')}>
          <h2 className={c.h}>{t('ci.h2')}</h2>
          <div className={c.cards}>
            {ISSUE_TEMPLATES.map(([code, label]) => {
              const g = gateOf(code, e);
              const on = code === tpl;
              return (
                <button
                  key={code}
                  type="button"
                  className={`${c.tpl} ${on ? c.tplOn : ''} ${g.block ? c.tplOff : ''}`}
                  aria-pressed={on}
                  aria-disabled={!!g.block}
                  onClick={() => (g.block ? toast({ msg: t('ci.cantUse', { x: g.block.split(' — ')[0] ?? '' }) }) : setTplPick(code))}
                >
                  <div className={c.tplHd}><b>{code}</b><span className={`${s.stChip} ${TONE.mute}`}>Draft</span></div>
                  <span className={c.sub}>{label}</span>
                  <span className={`${c.gate} ${g.block ? c.gErr : g.warns.length ? c.gWarn : c.gOk}`}>
                    {g.block || `${g.warns[0] ?? t('ci.valid')}${code === e[6] ? t('ci.hint') : ''}`}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
        <section className={s.card} aria-label={t('ci.a3')}>
          <h2 className={c.h}>{t('ci.h3')}</h2>
          <div className={c.fields}>
            {fields.map((f) => (
              <label key={f.k} className={c.lab}>
                <div>{f.k}</div>
                {f.key ? (
                  <input aria-label={f.k} className={c.ctl} value={f.val} onChange={(ev) => setInp({ ...inp, [f.key as 'sign' | 'from']: ev.target.value })} />
                ) : (
                  <div className={`${c.ctl} ${c.lock} ${f.mask ? c.mask : ''}`}>{f.val}</div>
                )}
              </label>
            ))}
          </div>
          {gateMsg && <div role="alert" className={`${c.alert} ${v.gate.block ? c.aErr : c.aWarn}`}>{gateMsg}</div>}
        </section>
      </div>
      <section className={`${s.card} ${c.side}`} aria-label={t('ci.aPv')}>
        <div className={c.pvHd}>
          <h2 className={c.h}>{t('ci.h4')}</h2>
          <span className={v.missAddr ? c.gErr : c.gOk}>{v.missAddr ? t('ci.missField') : t('ci.fullField')}</span>
        </div>
        <div className={c.paper}>
          <div className={c.ctr}><b>{t('ci.nation')}</b></div>
          <div className={`${c.ctr} ${c.sub}`}>{t('ci.motto')}</div>
          <div className={`${c.ctr} ${c.pvTitle}`}>{title}</div>
          <div className={`${c.ctr} ${c.sub}`}>{t('ci.no', { n: v.soHD })}</div>
          <div className={c.l0}><b>{t('ci.partyA')}</b> HANA LOGISTICS · 235 Cộng Hòa, Tân Bình, TP.HCM · {t('ci.repOf')} {hl(t('ci.rep'))}</div>
          <div><b>{t('ci.partyB')}</b> {hl(name)} · CCCD {hl(t('ci.addrC'))} · {t('ci.permAddr')} {v.missAddr ? <span className={c.miss}>{'{DiaChiThuongTru}'} {t('ci.missAddr')}</span> : hl(t('ci.addrC'))}</div>
          <div><b>{t('ci.art1')}</b> {t('ci.job')} {hl(`${e[7]} (${e[2].split(' · ')[0]})`)} · {t('cti.dept')} {hl(e[3])} · {t('ci.level')} {hl(e[2].split(' · ')[1] ?? '')}</div>
          <div><b>{t('ci.art2')}</b> {t('ci.term')} {hl(v.isKxd ? t('ci.indef') : t('ci.months', { n: v.months ?? 0 }))} · {t('ci.from')} {hl(v.from)}{!v.isKxd && <> {t('ci.until')} {hl(v.to)}</>}</div>
          <div><b>{t('ci.art3')}</b> {t('ci.hours')} {hl(workHoursText())} {t('ci.hoursSrc')}</div>
          <div><b>{t('ci.art4')}</b> {t('ci.salary')} {hl(t('ci.needMoney'))} · {t('ci.payDay')}</div>
          <div><b>{t('ci.art5')}</b> {t('ci.insure')}{tpl === 'HDTV-02M' ? t('ci.trialNoIns') : ''} · {t('ci.leave')} {hl(t('ci.days', { n: annualLeaveDays() ?? 0 }))}</div>
          <div><b>{t('ci.art6')}</b> {t('ci.end')}</div>
        </div>
        <div className={c.acts}>
          <Button icon={<IconPrinter size={16} aria-hidden="true" />} onClick={() => toast({ msg: t('ci.printing', { no: v.soHD }) })}>{t('ci.print')}</Button>
          <Button
            variant="primary"
            icon={<IconFilePlus size={16} aria-hidden="true" />}
            style={{ opacity: v.canIssue ? 1 : 0.55 }}
            onClick={() => toast({ msg: v.canIssue ? t('ci.noWrite') : v.gate.block ? t('ci.blocked', { x: v.gate.block.split(' — ')[0] ?? '' }) : t('ci.missToast') })}
          >
            {t('cti.save')}
          </Button>
        </div>
      </section>
    </div>
  );
}
